import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Componentes del Módulo
import TablaClientes from './TablaClientes';
import ModalCliente from './ModalCliente';

// Componentes Reutilizables de Interfaz
import PageHeader from '../../cpanel/PageHeader';
import BuscadorInteligente from '../../ui/BuscadorInteligente';
import AuthModal from '../../ui/AuthModal';

// Firebase Utils
import { deleteClientFull } from '../../../firebase/firebaseUtils';

const Clientes = () => {
    const navigate = useNavigate();
    
    // --- Estados Principales ---
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    
    // --- Estados de Modales ---
    const [showModal, setShowModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);
    const [notification, setNotification] = useState({ message: '', isError: false, show: false });
    
    const showNotification = (message, isError = false) => {
        setNotification({ message, isError, show: true });
        setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 3000);
    };

    // --- Callback del Buscador Inteligente ---
    const handleSearchResult = (results) => {
        setHasSearched(true);
        setClients(results);
        
        if (results.length > 0) {
            showNotification(`Se encontraron ${results.length} clientes.`, false);
        } else {
            showNotification('No se encontraron resultados.', false);
        }
    };

    // --- Lógica CRUD ---
    const handleEdit = (clientData) => {
        setEditingClient(clientData);
        setShowModal(true);
    };
    
    const handleAdd = () => {
        setEditingClient(null); 
        setShowModal(true);
    };

    const handleSaveSuccess = (message, savedClient) => {
        showNotification(message, false);
        setShowModal(false);
        if (editingClient) {
            setClients(prev => prev.map(c => c.id === savedClient.id ? savedClient : c));
        } else {
            setClients(prev => [savedClient, ...prev].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
        }
        setEditingClient(null);
    };

    const handleDeleteClick = (clientId) => {
        setClientToDelete(clientId);
        setShowDeleteModal(true);
    };

    const executeDelete = async () => {
        if (!clientToDelete) return;
        try {
            await deleteClientFull(clientToDelete);
            setClients(prev => prev.filter(c => c.id !== clientToDelete));
            showNotification('Cliente eliminado correctamente.', false);
        } catch (error) {
            console.error("Error deleting client:", error);
            showNotification('Error al eliminar el cliente.', true);
        } finally {
            setShowDeleteModal(false);
            setClientToDelete(null);
        }
    };
    
    // --- Renderizado Condicional del Contenido ---
    let mainContent;
    if (isLoading) {
        mainContent = (
            <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-slate-500 mt-4 text-xs font-bold uppercase tracking-wider animate-pulse">Buscando asegurados...</p>
            </div>
        );
    } else if (clients.length > 0) {
        mainContent = <TablaClientes clients={clients} onEdit={handleEdit} onDelete={handleDeleteClick} />;
    } else if (hasSearched) {
        mainContent = (
            <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
                <div className="bg-white p-6 rounded-3xl shadow-2xs border border-slate-200/70 mb-4">
                    <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-lg font-black text-slate-800 mb-1">No se encontraron resultados</h3>
                <p className="text-slate-400 text-xs max-w-sm">Intenta con otro término de búsqueda o verifica los datos del cliente.</p>
            </div>
        );
    } else {
        mainContent = (
            <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in max-w-xl mx-auto">
                <div className="bg-white rounded-3xl border border-slate-200/80 p-8 shadow-sm space-y-4 w-full">
                    <div className="p-3.5 bg-blue-50 border border-blue-100 rounded-2xl w-fit mx-auto text-blue-600">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>

                    <div className="space-y-1.5">
                        <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-full border border-blue-100 inline-block">
                            Cartera de Asegurados
                        </span>
                        <h3 className="text-xl font-black text-slate-900 tracking-tight">
                            Comienza tu Búsqueda
                        </h3>
                        <p className="text-slate-400 text-xs font-medium max-w-md mx-auto leading-relaxed">
                            Busca en el padrón por Nombre, Razón Social, DNI, CUIT o Teléfono para administrar perfiles.
                        </p>
                    </div>

                    <div className="pt-2 flex flex-wrap items-center justify-center gap-2">
                        <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                            👤 Nombre / Apellido
                        </span>
                        <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                            🏢 Razón Social
                        </span>
                        <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                            💳 DNI / CUIT
                        </span>
                        <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                            📞 Teléfono
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f3f4f8] font-sans text-left pb-16 w-full antialiased flex flex-col">
            
            {/* TOAST NOTIFICACIÓN */}
            {notification.show && (
                <div className={`fixed top-6 right-6 z-50 flex items-center p-4 rounded-2xl shadow-xl border transition-all duration-300 ${
                    notification.isError ? 'bg-white border-rose-200 text-rose-600' : 'bg-white border-emerald-200 text-emerald-700'
                }`}>
                    <div className={`p-2 rounded-xl mr-3 ${notification.isError ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                        {notification.isError ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </div>
                    <p className="font-bold text-xs uppercase tracking-wide">{notification.message}</p>
                </div>
            )}

            {/* ENCABEZADO REUTILIZABLE DE LA PÁGINA */}
            <PageHeader 
                title="Administrador de Clientes"
                subtitle="Gestión integral de cartera, perfiles y estados de cuenta"
                topBorderGradient="from-blue-600 via-indigo-600 to-purple-600"
                buttons={[
                    {
                        label: 'Volver al Cpanel',
                        onClick: () => navigate('/cpanel'),
                        variant: 'secondary',
                        icon: (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        )
                    },
                    {
                        label: 'Nuevo Cliente',
                        onClick: handleAdd,
                        variant: 'primary',
                        icon: (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                        )
                    }
                ]}
                showBackButton={false}
            />

            {/* CONTENIDO PRINCIPAL DIRECTO EN EL FONDO */}
            <div className="w-full px-8 pt-8 max-w-[1500px] mx-auto space-y-6 flex-grow">
                
                {/* BUSCADOR UNIFICADO (ANCHO max-w-5xl) */}
                <div className="bg-white p-6 rounded-3xl shadow-2xs border border-slate-200/80 max-w-5xl mx-auto text-left">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-black text-slate-900 tracking-tight">
                                Buscar Cliente
                            </h2>
                            <p className="text-xs text-slate-400 font-medium mt-0.5">
                                Encuentra asegurados por Nombre, Razón Social, DNI, CUIT o Teléfono
                            </p>
                        </div>
                        <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-xl text-blue-600 hidden sm:block">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                    
                    <BuscadorInteligente 
                        scope="clientes"
                        onResult={handleSearchResult}
                        onLoading={setIsLoading}
                        placeholder="Ej: Juan Perez, 20304050601, 1122334455..."
                    />
                </div>

                {/* CONTENIDO DINÁMICO DE CLIENTES */}
                <main className="transition-all duration-300">
                    {mainContent}
                </main>

                {/* MODAL DE EDICIÓN Y CREACIÓN */}
                <ModalCliente 
                    show={showModal} 
                    client={editingClient} 
                    onClose={() => setShowModal(false)} 
                    onSaveSuccess={handleSaveSuccess} 
                    setNotification={showNotification} 
                />

                {/* AUTH MODAL DE SEGURIDAD PARA ELIMINACIÓN */}
                <AuthModal 
                    isOpen={showDeleteModal} 
                    onClose={() => { setShowDeleteModal(false); setClientToDelete(null); }} 
                    onConfirm={executeDelete} 
                    title="Eliminar Cliente" 
                    message="¿Estás seguro de que quieres eliminar este cliente? Esta acción borrará TODAS sus pólizas, siniestros y datos asociados de forma permanente." 
                    requireAuth={true} 
                />

            </div>

            {/* FOOTER */}
            <footer className="mt-16 pt-6 border-t border-slate-200/80 text-center select-none shrink-0">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    &copy; {new Date().getFullYear()} IB BROKERS SYSTEM &bull; INTELLIGENT INSURANCE MANAGEMENT &bull; v2.5
                </p>
            </footer>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default Clientes;